'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import ChevronIcon from '@/fo/icons/base/icons/chevron16.svg';
import CrossIcon from '@/fo/icons/base/icons/cross24.svg';
import CrossLIcon from '@/fo/icons/base/icons/cross32.svg';
import HandbagIcon from '@/fo/icons/base/icons/handbag.svg';
import HeartIcon from '@/fo/icons/base/icons/header-heart.svg';
import MenuIcon from '@/fo/icons/base/icons/menu.svg';
import SearchIcon from '@/fo/icons/base/icons/search-header.svg';
import UserIcon from '@/fo/icons/base/icons/user.svg';
import BiodermaLogo from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermLogo from '@/fo/icons/base/logos/esthederm.svg';
import EtatPurLogo from '@/fo/icons/base/logos/etat-pur.svg';
import NaosLogo from '@/fo/icons/base/logos/naos.svg';
import {
  storeProduct,
  selectCounterWishlist,
  selectCounterCart
} from '@/fo/store/product';
import { MenuItem, UiAppMenuBase } from '@/fo/ui/app/menu/base';
import { eventBannerHero } from '@/fo/ui/kit/banner/hero';
import {
  UiKitCounterBase,
  UiKitCounterBaseProps
} from '@/fo/ui/kit/counter/base';
import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiModSliderFading } from '@/fo/ui/mod/slider/fading';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCityModal } from '@/fo/util/event-city-modal';

import { styles } from './UiAppHeaderBase.styles';

type Variants = VariantProps<typeof styles>;

const iconsNavigate = {
  search: () => <SearchIcon />,
  profile: () => <UserIcon />,
  favorite: () => <HeartIcon />,
  cart: () => <HandbagIcon />
};

type Icon = {
  icon: keyof typeof iconsNavigate;
  label?: string;
  counter?: number;
  noLimit?: boolean;
  onClick?: () => void;
};

export interface UiAppHeaderBaseProps {
  color?: Variants['color'];
  informer?: string;
  rightLink?: { href: string; label: string };
  city: {
    label: string;
  };
  brands: {
    naos: { href: string; onClick?: () => void };
    bioderma: { href: string; onClick?: () => void };
    esthederm: { href: string; onClick?: () => void };
    etatPur: { href: string; onClick?: () => void };
  };
  links?: { href: string; label: string; onClick?: () => void }[];
  menu: {
    categories: MenuItem[];
    brands: MenuItem[];
    diagnosticArticles: MenuItem[];
    skinArticles: MenuItem[];
  };
  isAuthorized: boolean;
  searchSuggestion?: React.ReactNode;
  loyaltyBalance?: number;
}

const collectSeoLinks = (
  items: MenuItem[] = [],
  result: { href: string; label: string }[] = []
) => {
  items.forEach((item) => {
    result.push({ href: item.href, label: item.label });
    if (item.children?.length) {
      collectSeoLinks(item.children, result);
    }
  });
  return result;
};

export function UiAppHeaderBase({
  color: colorProp = 'base',
  informer,
  rightLink,
  city,
  brands,
  links = [],
  menu,
  isAuthorized,
  searchSuggestion,
  loyaltyBalance
}: UiAppHeaderBaseProps) {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const refTimer = useRef(0);

  const isMobile = useMediaQuery('md', 'max');

  const [isFixed, setIsFixed] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [activeMenu, setActiveMenu] = useState({
    index: -1,
    offsetLeft: 0
  });
  const refHeroBannerColor = useRef<Variants['color']>(colorProp);
  const [color, setColor] = useState<Variants['color']>(colorProp);

  const showOverlayMenu = isMobile
    ? false
    : activeMenu.index > -1 || showSearch;

  // Не делайте так, это плохой пример, не используйте store в ui компонентах
  const wishlistCount = useSelector(storeProduct, selectCounterWishlist);
  const cartCount = useSelector(storeProduct, selectCounterCart);

  const icons: Icon[] = [
    {
      icon: 'search',
      label: t('find'),
      onClick: () => handleShowSearch()
    },
    {
      icon: 'profile',
      counter: isAuthorized ? loyaltyBalance : undefined,
      noLimit: true,
      onClick: () =>
        isAuthorized
          ? router.push('/account')
          : utilEventAuthModal.open('header')
    },
    {
      icon: 'favorite',
      counter: wishlistCount,
      onClick: () =>
        isAuthorized
          ? router.push('/account/wishlist')
          : utilEventAuthModal.open('header')
    },
    {
      icon: 'cart',
      counter: cartCount,
      onClick: () => router.push('/cart')
    }
  ];

  useEffect(() => {
    setShowSearch(false);
    setActiveMenu({ index: -1, offsetLeft: 0 });
  }, [isMobile, pathname, searchParams]);

  useEffect(() => {
    const modeToColor = {
      light: 'base',
      dark: 'transparent',
      off: colorProp
    } as const;
    return eventBannerHero.onModeChange((mode = 'off') => {
      refHeroBannerColor.current = modeToColor[mode] || colorProp;
      if (!showOverlayMenu) {
        setColor(refHeroBannerColor.current);
      }
    });
  }, [colorProp, showOverlayMenu]);

  const slots = styles({ color, fixed: isFixed, shadow: showOverlayMenu });

  const colorCounter: UiKitCounterBaseProps['color'] =
    color === 'transparent' && !isFixed ? 'light' : 'primary';

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsMount(true);
    });
    return () => {
      clearTimeout(refTimer.current);
    };
  }, []);

  const handleChangeMenu = (state: typeof activeMenu, delay = 150) => {
    if (showSearch) {
      setShowSearch(false);
    }
    clearTimeout(refTimer.current);
    refTimer.current = window.setTimeout(() => {
      if (state.index > -1) {
        setColor('white');
      } else {
        setColor(refHeroBannerColor.current);
      }
      setActiveMenu((s) => ({ ...s, ...state }));
    }, delay);
  };

  const handleHideMenu = () => {
    handleChangeMenu({ index: -1, offsetLeft: 0 });
  };

  const toggleMenu = (state: typeof activeMenu) => {
    if (state.index === activeMenu.index) {
      handleHideMenu();
    } else {
      handleChangeMenu(state);
    }
  };

  const handleShowSearch = () => {
    const next = !showSearch;
    setShowSearch(next);
    if (next) {
      setColor('white');
    } else {
      setColor(refHeroBannerColor.current);
    }
  };

  const handleOverlayBackgroundClick = () => {
    if (showSearch) {
      setShowSearch(false);
    }
    if (activeMenu.index > -1) {
      handleHideMenu();
    }
  };

  const topRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const topLogoRef = useRef<HTMLDivElement | null>(null);
  const seoLinks = [...links, ...(rightLink ? [rightLink] : [])].concat(
    collectSeoLinks(menu.categories),
    collectSeoLinks(menu.brands),
    collectSeoLinks(menu.diagnosticArticles),
    collectSeoLinks(menu.skinArticles)
  );

  // Императивно изменяя стили мы ускоряем их применение к элементам хедера
  const handleChangeFixed = (isFixed: boolean) => {
    setIsFixed(isFixed); // Чтобы после rerender синхронизировать стили в jsx элементах

    const newSlots = styles({
      color,
      fixed: isFixed,
      shadow: showOverlayMenu
    });

    if (topRef.current) {
      topRef.current.classList.remove(
        ...slots
          .top({
            className: isMount ? '' : 'max-md:opacity-0'
          })
          .split(' ')
      );

      topRef.current.classList.add(
        ...newSlots
          .top({
            className: isMount ? '' : 'max-md:opacity-0'
          })
          .split(' ')
      );
    }

    if (mainRef.current) {
      mainRef.current.classList.remove(
        ...slots
          .main({
            className: isMount ? '' : 'opacity-0'
          })
          .split(' ')
      );

      mainRef.current.classList.add(
        ...newSlots
          .main({
            className: isMount ? '' : 'opacity-0'
          })
          .split(' ')
      );
    }

    if (topLogoRef.current) {
      topLogoRef.current.classList.remove(...slots.topLogo().split(' '));

      topLogoRef.current.classList.add(...newSlots.topLogo().split(' '));
    }
  };

  return (
    <div id="app-header">
      {informer && (
        <UiModSliderFading
          className={slots.informer({
            className: isMount ? '' : 'max-md:opacity-0'
          })}
        >
          {informer}
        </UiModSliderFading>
      )}
      <UiModHeadFixed
        ref={topRef}
        className={slots.top({ className: isMount ? '' : 'max-md:opacity-0' })}
        onFixedChange={handleChangeFixed}
        order={0}
      >
        <div className={slots.container()}>
          <div
            className={slots.topLeftLink({
              className: isMount ? '' : 'opacity-0'
            })}
            onClick={() => utilEventCityModal.open()}
          >
            <span
              title={city.label}
              className={slots.link()}
            >
              {city.label}
            </span>
            <ChevronIcon
              className={slots.linkArrow({ className: 'rotate-90' })}
            />
          </div>
          <div className={slots.topLogo()}>
            <Link
              href={brands.naos.href}
              onClick={brands.naos.onClick}
              className={slots.logoNaos()}
            >
              <NaosLogo className="h-6 w-28 md:h-8 md:w-37.75" />
            </Link>
            <Link
              href={brands.bioderma.href}
              onClick={brands.bioderma.onClick}
              className={slots.logoBioderma()}
            >
              <BiodermaLogo />
            </Link>
            <Link
              href={brands.esthederm.href}
              onClick={brands.esthederm.onClick}
              className={slots.logoEsthederm()}
            >
              <EsthedermLogo />
            </Link>
            <Link
              href={brands.etatPur.href}
              onClick={brands.etatPur.onClick}
              className={slots.logoEtatPur()}
            >
              <EtatPurLogo />
            </Link>
          </div>
          <div
            className={slots.topRightLink({
              className: isMount ? '' : 'opacity-0'
            })}
          >
            {rightLink && (
              <Link
                href={rightLink.href}
                className="flex-center"
              >
                <span>{rightLink.label}</span>
                <ChevronIcon className={slots.linkArrow()} />
              </Link>
            )}
          </div>
        </div>
      </UiModHeadFixed>
      <UiModHeadFixed
        ref={mainRef}
        className={slots.main({ className: isMount ? '' : 'opacity-0' })}
        onFixedChange={handleChangeFixed}
        order={0}
      >
        <div className={slots.container()}>
          <ArrowIcon
            className={slots.backArrow()}
            // onClick={onBackClick}
          />
          <div
            className={slots.linksLeft()}
            data-nosnippet
          >
            <div
              className={slots.linkCatalog({
                className: activeMenu.index === 0 ? 'after:w-full' : ''
              })}
              onClick={(event) => {
                if (activeMenu.index === 0) {
                  router.push('/catalog');
                } else {
                  toggleMenu({
                    index: 0,
                    offsetLeft: event.currentTarget?.offsetLeft || 0
                  });
                }
              }}
              onMouseEnter={(e) => {
                handleChangeMenu({
                  index: 0,
                  offsetLeft: e.currentTarget?.offsetLeft || 0
                });
              }}
              onMouseLeave={handleHideMenu}
            >
              <div
                className="flex-center size-[40px]"
                onClick={(event) => {
                  if (activeMenu.index === 0) {
                    event.stopPropagation();
                    handleHideMenu();
                  }
                }}
              >
                {activeMenu.index === 0 ? <CrossLIcon /> : <MenuIcon />}
              </div>
              <span className={slots.linkLabel()}>{t('catalog')}</span>
            </div>
            {links.map((link, index) => (
              <Link
                key={index}
                href={link.href}
                className={slots.linkItem({
                  className:
                    activeMenu.index === index + 1 ? 'after:w-full' : ''
                })}
                onClick={link.onClick}
                onMouseEnter={(e) => {
                  handleChangeMenu({
                    index: index + 1,
                    offsetLeft: e.currentTarget?.offsetLeft || 0
                  });
                }}
                onMouseLeave={handleHideMenu}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <Link
            href={brands.naos.href}
            onClick={brands.naos.onClick}
            className={slots.mainLogo()}
          >
            <NaosLogo />
          </Link>
          <div className={slots.linksRight()}>
            {icons.map((icon, index) => (
              <div
                key={`${icon.icon}-${index}`}
                className={slots.linkIcon()}
                onClick={icon.onClick}
                data-mobile-app-hidden={
                  icon.icon === 'favorite' || icon.icon === 'cart'
                    ? 'commerce'
                    : undefined
                }
              >
                {iconsNavigate[icon.icon]()}
                <UiKitCounterBase
                  prefix={
                    icon.icon === 'profile' && isAuthorized
                      ? 'marker'
                      : undefined
                  }
                  color={icon.icon === 'profile' ? 'yellow' : colorCounter}
                  noLimit={icon.noLimit}
                  className={slots.linkCounter()}
                  counter={icon.counter}
                />
                {icon.label && (
                  <span className={slots.linkLabel()}>{icon.label}</span>
                )}
              </div>
            ))}
          </div>
          <CrossIcon
            className={slots.crossArrow()}
            // onClick={onCrossClick}
          />
        </div>
      </UiModHeadFixed>
      <div
        className={slots.menuOverlay({
          className: showOverlayMenu ? '' : 'hidden'
        })}
        onClick={handleOverlayBackgroundClick}
      >
        <nav
          className="sr-only"
          aria-label={t('Seo navigation')}
        >
          {seoLinks.map((item) => (
            <Link
              key={`${item.href}-${item.label}`}
              href={item.href}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div onClick={(event) => event.stopPropagation()}>
          {activeMenu.index === 0 && (
            <UiAppMenuBase
              items={menu.categories}
              additionalItems={menu.brands}
              style={{ marginLeft: `${activeMenu.offsetLeft}px` }}
              onShow={() => handleChangeMenu(activeMenu)}
              onHide={handleHideMenu}
            />
          )}
          {activeMenu.index === 1 && (
            <UiAppMenuBase
              items={menu.diagnosticArticles}
              style={{ marginLeft: `${activeMenu.offsetLeft}px` }}
              onShow={() => handleChangeMenu(activeMenu)}
              onHide={handleHideMenu}
            />
          )}
          {activeMenu.index === 2 && (
            <UiAppMenuBase
              items={menu.skinArticles}
              style={{ marginLeft: `${activeMenu.offsetLeft}px` }}
              onShow={() => handleChangeMenu(activeMenu)}
              onHide={handleHideMenu}
            />
          )}
          {showSearch && (
            <div className={slots.searchSuggestion()}>{searchSuggestion}</div>
          )}
        </div>
      </div>
    </div>
  );
}
